
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './CommandCenter.css';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CustomDropdown from './CustomDropdown';
import { FriendsIcon } from './friends';
import { SettingsMenu } from './settings';
import { useMusic } from './hooks/useMusic';
import { playSoundEffect } from './hooks/useSoundEffects';
import { API_URL } from './config/apiConfig';
import { useContainerScale } from './hooks/useViewportScale';
import { TutorialMessageBox } from './tutorial';

const factionColors = {
  'Remnants of Humanity': '#556B2F',
  'Exolot Dominion': '#4B0082',
  'League of Shadows': '#1A1A1A',
  'Ra\'s Children': '#F4A460',
  'Asrah': '#8B0000',
  'The Sentients': '#ADD8E6',
  'Combined': '#FFA500'
};

const combinedFactions = {
  'The Conspiracy': ['The Conspiracy', 'League of Shadows', 'Asrah', 'The Sentients'],
  'The Corrupted Core': ['The Corrupted Core', 'Exolot Dominion', 'Asrah', 'The Sentients'],
  'The Covenant': ['The Covenant', 'Ra\'s Children', 'League of Shadows', 'The Sentients'],
  'The Divine Syndicate': ['The Divine Syndicate', 'Ra\'s Children', 'Exolot Dominion', 'The Sentients'],
  'The Forsaken Pact': ['The Forsaken Pact', 'Remnants of Humanity', 'Asrah', 'The Sentients'],
  'The Hollow': ['The Hollow', 'Ra\'s Children', 'Asrah', 'The Sentients'],
  'The Iron Alliance': ['The Iron Alliance', 'Remnants of Humanity', 'Exolot Dominion', 'The Sentients'],
  'The Last Crusade': ['The Last Crusade', 'Ra\'s Children', 'Remnants of Humanity', 'The Sentients'],
  'The Shadow Consortium': ['The Shadow Consortium', 'League of Shadows', 'Exolot Dominion', 'The Sentients'],
  'The Silent Accord': ['The Silent Accord', 'Remnants of Humanity', 'League of Shadows', 'The Sentients']
};

function CommandCenter({ user, onLogout }) {
  const { t, i18n: i18nInstance } = useTranslation();
  
  // Viewport scaling for responsive design
  const { 
    scale: viewportScale, 
    offsetX: viewportOffsetX, 
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile
  } = useContainerScale();
  
  // Play find strategies music
  useMusic('/music/Epic_2.mp3');
  const [strategies, setStrategies] = useState([]);
  const [editPopup, setEditPopup] = useState(false);
  const [editStrategy, setEditStrategy] = useState(null);
  const [editDescription, setEditDescription] = useState('');
  const [editIsPublic, setEditIsPublic] = useState(true);
  const navigate = useNavigate();
  const [factionTranslations, setFactionTranslations] = useState({}); // Map: english_name -> translated_name

  // Filter and sort state
  const [nameSearch, setNameSearch] = useState('');
  const [creatorSearch, setCreatorSearch] = useState('');
  const [factionFilter, setFactionFilter] = useState('all');
  const [factionSort, setFactionSort] = useState(null); // null, 'asc', 'desc'
  const [createdAtSort, setCreatedAtSort] = useState('desc'); // 'asc', 'desc'
  const [showTutorialCommandCenter, setShowTutorialCommandCenter] = useState(false);
  const [seenTutorialMessages, setSeenTutorialMessages] = useState([]);

  // Get faction orb background color/gradient
  const getFactionOrbBackground = (factionName) => {
    if (!factionName || factionName === 'Unknown') {
      return '#ccc';
    }

    // Check if it's a combined faction and return gradient
    if (factionName === 'The Conspiracy') {
      return 'linear-gradient(to right, #8B0000 50%, #1A1A1A 50%)';
    } else if (factionName === 'The Corrupted Core') {
      return 'linear-gradient(to right, #4B0082 50%, #8B0000 50%)';
    } else if (factionName === 'The Covenant') {
      return 'linear-gradient(to right, #F4A460 50%, #1A1A1A 50%)';
    } else if (factionName === 'The Divine Syndicate') {
      return 'linear-gradient(to right, #F4A460 50%, #4B0082 50%)';
    } else if (factionName === 'The Forsaken Pact') {
      return 'linear-gradient(to right, #556B2F 50%, #8B0000 50%)';
    } else if (factionName === 'The Hollow') {
      return 'linear-gradient(to right, #F4A460 50%, #8B0000 50%)';
    } else if (factionName === 'The Iron Alliance') {
      return 'linear-gradient(to right, #556B2F 50%, #4B0082 50%)';
    } else if (factionName === 'The Last Crusade') {
      return 'linear-gradient(to right, #F4A460 50%, #556B2F 50%)';
    } else if (factionName === 'The Shadow Consortium') {
      return 'linear-gradient(to right, #1A1A1A 50%, #4B0082 50%)';
    } else if (factionName === 'The Silent Accord') {
      return 'linear-gradient(to right, #556B2F 50%, #1A1A1A 50%)';
    }

    // Return base faction color
    return factionColors[factionName] || '#ccc';
  };

  const fetchStrategies = () => {
    const token = localStorage.getItem('token');
    const currentLanguage = i18nInstance.language || 'en-us';
    const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
    fetch(`${API_URL}/all-strategies?lang=${encodeURIComponent(normalizedLanguage)}`, {
      headers: { 'Authorization': token ? `Bearer ${token}` : '' }
    })
      .then(response => response.json())
      .then(data => setStrategies(Array.isArray(data) ? data : []))
      .catch(error => console.error('Error fetching strategies:', error));
  };

  // Fetch faction translations
  useEffect(() => {
    const fetchFactionTranslations = async () => {
      try {
        const currentLanguage = i18nInstance.language || 'en-us';
        const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
        
        const response = await fetch(`${API_URL}/factions?lang=${encodeURIComponent(normalizedLanguage)}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const factions = await response.json();
          const translationMap = {};
          factions.forEach(faction => {
            translationMap[faction.english_name] = faction.name;
          });
          setFactionTranslations(translationMap);
        }
      } catch (err) {
        console.error('Failed to fetch faction translations:', err);
        // Fallback: use English names
        setFactionTranslations({});
      }
    };
    
    fetchFactionTranslations();
  }, [i18nInstance.language]);

  useEffect(() => {
    fetchStrategies();
  }, [i18nInstance.language]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_URL}/tutorial/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) setSeenTutorialMessages(data.seenMessages || []);
      })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (user && !seenTutorialMessages.includes('command_center')) {
      const timer = setTimeout(() => setShowTutorialCommandCenter(true), 600);
      return () => clearTimeout(timer);
    }
  }, [user, seenTutorialMessages]);

  const handleEditClick = (strategy) => {
    setEditStrategy(strategy);
    setEditDescription(strategy.description || '');
    setEditIsPublic(!strategy.is_private);
    setEditPopup(true);
  };

  const handleEditSubmit = () => {
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/strategy/${editStrategy.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : ''
      },
      body: JSON.stringify({
        description: editDescription,
        is_private: editIsPublic
      })
    })
      .then(res => {
        if (res.ok) {
          setEditPopup(false);
          fetchStrategies();
        } else {
          alert(t('marketplace.updateFailed'));
        }
      });
  };

  const handleSubscribe = async (strategy) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/subscribe-strategy/${strategy.id}`, {
      method: 'POST',
      headers: { 'Authorization': token ? `Bearer ${token}` : '' }
    });
    if (res.ok) {
      fetchStrategies();
    } else {
      alert(t('marketplace.subscribeFailed'));
    }
  };

const handleUnsubscribe = async (strategy) => {
  const token = localStorage.getItem('token');
const originalId = strategy.original_strategy_id;

if (!originalId) {
  console.error("Missing original_strategy_id for unsubscribe:", strategy);
  alert(t('marketplace.unsubscribeError'));
  return;
}

    console.log("Unsubscribing from original strategy ID:", originalId);
	console.log("Strategy to unsubscribe:", strategy);

  const res = await fetch(`${API_URL}/unsubscribe-strategy/${originalId}`, {
    method: 'DELETE',
    headers: { 'Authorization': token ? `Bearer ${token}` : '' }
  });
  if (res.ok) {
    fetchStrategies();
  } else {
    alert(t('marketplace.unsubscribeFailed'));
  }
};

  // Get all unique factions for filter dropdown
  const allFactions = React.useMemo(() => {
    const factions = new Set();
    strategies.forEach(s => {
      if (s.faction_name && s.faction_name !== 'Unknown') {
        factions.add(s.faction_name);
      }
    });
    return Array.from(factions).sort();
  }, [strategies]);

  // Filter and sort strategies
  const filteredAndSortedStrategies = React.useMemo(() => {
    let filtered = [...strategies];

    // Apply name search
    if (nameSearch.trim()) {
      filtered = filtered.filter(s => 
        s.name.toLowerCase().includes(nameSearch.toLowerCase())
      );
    }

    // Apply creator search
    if (creatorSearch.trim()) {
      filtered = filtered.filter(s => 
        s.username.toLowerCase().includes(creatorSearch.toLowerCase())
      );
    }

    // Apply faction filter
    if (factionFilter !== 'all') {
      filtered = filtered.filter(s => s.faction_name === factionFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      // Sort by faction if faction sort is active
      if (factionSort) {
        const aFaction = a.faction_name || 'Unknown';
        const bFaction = b.faction_name || 'Unknown';
        const comparison = aFaction.localeCompare(bFaction);
        return factionSort === 'asc' ? comparison : -comparison;
      }

      // Sort by created_at
      const aDate = new Date(a.created_at);
      const bDate = new Date(b.created_at);
      return createdAtSort === 'asc' 
        ? aDate - bDate 
        : bDate - aDate;
    });

    return filtered;
  }, [strategies, nameSearch, creatorSearch, factionFilter, factionSort, createdAtSort]);

  const handleFactionSort = () => {
    if (factionSort === null) {
      setFactionSort('asc');
      setCreatedAtSort('desc'); // Reset created_at sort
    } else if (factionSort === 'asc') {
      setFactionSort('desc');
    } else {
      setFactionSort(null);
      setCreatedAtSort('desc'); // Default to desc when clearing
    }
  };

  const handleCreatedAtSort = () => {
    if (createdAtSort === 'desc') {
      setCreatedAtSort('asc');
      setFactionSort(null); // Reset faction sort
    } else {
      setCreatedAtSort('desc');
    }
  };



  return (
    <div className="command-center-viewport-wrapper">
      <div 
        className="command-center-viewport-container"
        style={{
          width: `${containerBaseWidth}px`,
          height: `${containerBaseHeight}px`,
          transformOrigin: '0 0',
          transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
        }}
      >
        <div className={`command-center-container ${isContainerMobile ? 'mobile' : ''}`}>
          <div style={{ 
            position: 'absolute', 
            top: isContainerMobile ? 5 : 10, 
            left: isContainerMobile ? 5 : 10, 
            zIndex: 1000, 
            display: 'flex', 
            alignItems: 'center',
            transform: isContainerMobile ? 'scale(0.7)' : 'none',
            transformOrigin: 'top left'
          }}>
            <FriendsIcon />
            <SettingsMenu onLogout={() => {
              playSoundEffect('/sounds/click.mp3');
              onLogout();
              navigate('/');
            }} />
            <span style={{ color: 'white', marginRight: '10px', fontSize: isContainerMobile ? '10px' : 'inherit' }}>{user}</span>
          </div>
          
          <button onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            navigate('/welcome');
          }} className="back-button">
            {t('common.back')}
          </button>
      
      <h2>{t('marketplace.title')}</h2>
      
      {/* Search and Filter Controls */}
      <div className="command-center-controls">
        <div className="search-controls">
          <input
            type="text"
            placeholder={t('marketplace.searchByName')}
            value={nameSearch}
            onChange={(e) => setNameSearch(e.target.value)}
            className="search-input"
            style={isContainerMobile ? { minWidth: '120px', fontSize: '11px', padding: '4px 6px' } : {}}
          />
          <input
            type="text"
            placeholder={t('marketplace.searchByCreator')}
            value={creatorSearch}
            onChange={(e) => setCreatorSearch(e.target.value)}
            className="search-input"
            style={isContainerMobile ? { minWidth: '120px', fontSize: '11px', padding: '4px 6px' } : {}}
          />
          <div style={{ minWidth: isContainerMobile ? '150px' : '350px' }}>
            <CustomDropdown
              options={[
                { value: 'all', label: t('marketplace.allFactions') },
                ...allFactions.map(faction => ({ value: faction, label: faction }))
              ]}
              value={factionFilter}
              onChange={(value) => {
                playSoundEffect('/sounds/click.mp3');
                setFactionFilter(value);
              }}
              placeholder={t('marketplace.selectFaction')}
              renderOption={(option) => {
                if (option.value === 'all') {
                  return <span>{option.label}</span>;
                }
                const factionName = option.value;
                const translatedFactionName = factionTranslations[factionName] || factionName;
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
                    <div
                      className="faction-orb"
                      style={{ background: getFactionOrbBackground(factionName) }}
                      title={translatedFactionName}
                    />
                    <span>{translatedFactionName}</span>
                  </div>
                );
              }}
            />
          </div>
        </div>
      </div>

      <table className="strategy-table">
        <thead>
          <tr>
            <th>{t('marketplace.name')}</th>
            <th>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {t('marketplace.faction')}
                <button 
                  onClick={() => {
                    playSoundEffect('/sounds/click.mp3');
                    handleFactionSort();
                  }}
                  className="sort-button"
                  title={factionSort === 'asc' ? 'Sort descending' : factionSort === 'desc' ? 'Clear sort' : 'Sort ascending'}
                >
                  {factionSort === 'asc' ? '↑' : factionSort === 'desc' ? '↓' : '⇅'}
                </button>
                {factionFilter !== 'all' && (
                  <span className="filter-indicator" title={`Filtered: ${factionFilter}`}>🔍</span>
                )}
              </div>
            </th>
            <th>{t('marketplace.description')}</th>
            <th>{t('marketplace.creator')}</th>
            <th>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {t('marketplace.createdAt')}
                <button 
                  onClick={() => {
                    playSoundEffect('/sounds/click.mp3');
                    handleCreatedAtSort();
                  }}
                  className="sort-button"
                  title={createdAtSort === 'asc' ? 'Sort descending' : 'Sort ascending'}
                >
                  {createdAtSort === 'asc' ? '↑' : '↓'}
                </button>
              </div>
            </th>
            <th>{t('marketplace.action')}</th>
          </tr>
        </thead>
        <tbody>
          {filteredAndSortedStrategies.map(strategy => (
            <tr key={strategy.id}>
              <td>{strategy.name}</td>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div
                    className="faction-orb"
                    style={{ background: getFactionOrbBackground(strategy.faction_name) }}
                    title={strategy.faction_name_translated || factionTranslations[strategy.faction_name] || strategy.faction_name || 'Unknown'}
                  />
                  <span>{strategy.faction_name_translated || factionTranslations[strategy.faction_name] || strategy.faction_name || 'Unknown'}</span>
                </div>
              </td>
              <td>{strategy.description}</td>
              <td>{strategy.username}</td>
              <td>{new Date(strategy.created_at).toLocaleString()}</td>
              <td>
                {strategy.source === "duplicate" ? (
                  <span style={{ color: "#aaa", fontWeight: "bold" }}>{t('marketplace.duplicate')}</span>
                ) : strategy.source === "Subscribed" ? (
                  <button onClick={() => {
                    playSoundEffect('/sounds/click.mp3');
                    handleUnsubscribe(strategy);
                  }}>{t('marketplace.unsubscribe')}</button>
                ) : strategy.username === user ? (
                  <button onClick={() => {
                    playSoundEffect('/sounds/click.mp3');
                    handleEditClick(strategy);
                  }}>{t('marketplace.edit')}</button>
                ) : (
                  <button onClick={() => {
                    playSoundEffect('/sounds/click.mp3');
                    handleSubscribe(strategy);
                  }}>{t('marketplace.subscribe')}</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editPopup && (
        <div className="popup-overlay">
          <div className="popup">
            <h3>{t('marketplace.editStrategy')}</h3>
            <label>{t('marketplace.editDescription')}</label>
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              rows={4}
              style={{ width: '100%' }}
            />
            <div>
              <label>
                <input
                  type="checkbox"
                  checked={editIsPublic}
                  onChange={(e) => setEditIsPublic(e.target.checked)}
                />
                {t('marketplace.public')}
              </label>
            </div>
            <button onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              handleEditSubmit();
            }}>{t('marketplace.save')}</button>
            <button onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              setEditPopup(false);
            }}>{t('marketplace.cancel')}</button>
          </div>
        </div>
      )}

      {showTutorialCommandCenter && createPortal(
        <TutorialMessageBox
          image="/images/tutorial_explain.png"
          text={t('marketplace.tutorialText')}
          onContinue={async () => {
            const token = localStorage.getItem('token');
            if (token) {
              await fetch(`${API_URL}/tutorial/mark-seen`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ messageKey: 'command_center' }),
              });
              setSeenTutorialMessages(prev => prev.includes('command_center') ? prev : [...prev, 'command_center']);
            }
            setShowTutorialCommandCenter(false);
          }}
        />,
        document.body
      )}
        </div>
      </div>
    </div>
  );
}

export default CommandCenter;

